import React, { useState, useEffect, useContext } from 'react';
import DataTable from 'react-data-table-component';
import { useParams } from 'react-router-dom';
import { AppSettings } from './../../config/app-settings.js';
import { apiFetch } from '../../utils/api';

// 🎨 Color Admin (Bootstrap 5) のテーマに合わせるカスタムスタイル
const customDataTableStyles = {
  table: { style: { backgroundColor: 'transparent' } },
  headRow: {
    style: {
      backgroundColor: 'transparent',
      borderBottom: '2px solid #dadfc1',
      minHeight: '45px',
    },
  },
  headCells: { style: { fontSize: '13px', fontWeight: 'bold', color: '#1f2d3d', paddingLeft: '16px', paddingRight: '16px' } },
  rows: {
    style: { fontSize: '13px', color: '#212529', minHeight: '70px', borderBottom: '1px solid #e3e6ec', backgroundColor: '#ffffff' },
    stripedStyle: { backgroundColor: '#f8f9fa', color: '#212529' },
    highlightOnHoverStyle: { backgroundColor: '#e9ecef !important', transitionDuration: '0.1s', borderBottomColor: '#e3e6ec', outline: 'none' },
  },
  pagination: { style: { borderTop: '1px solid #e3e6ec', fontSize: '13px', color: '#495057' } },
  cells: { style: { display: 'flex', alignItems: 'center' } },
  expanderCell: { style: { display: 'flex', justifyContent: 'center', alignItems: 'center' } },
  expanderButton: { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'transparent', color: '#6c757d', '&:disabled': { display: 'none' }, '&:hover:not(:disabled)': { backgroundColor: '#e9ecef' } } },
};

// 行を展開（クリック）した時に表示する「店舗からの返信」コンポーネント
const ExpandedComponent = ({ data }) => (
  <div className="bg-light p-3" style={{ borderBottom: '1px solid #e3e6ec' }}>
    <h6 className="mb-2 text-primary"><i className="fa fa-info-circle me-1"></i>店舗からの返信:</h6>
    {data.shop_reply && (
      <pre className="m-0 bg-white p-2 border rounded" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
        <div>{data.shop_reply}</div>
      </pre>
    )}
  </div>
);

// 🎨 データテーブルのカラム（列）定義
const columns = [
  {
    name: 'イメージ',
    width: '120px',
    cell: row => {
      const shopDefaultImage = "/assets/img/brand/shop.svg";
      const imgElement = (
        <img
          src={row.is_shop_review ? shopDefaultImage : (row.image_url || shopDefaultImage)}
          alt="対象イメージ"
          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px', margin: '5px 0', border: '1px solid #eee', cursor: row.product_url ? 'pointer' : 'default' }}
        />
      );
      if (!row.is_shop_review && row.product_url) {
        return <a href={row.product_url} target="_blank" rel="noreferrer" title="商品ページを開く">{imgElement}</a>;
      }
      return imgElement;
    }
  },
  {
    name: 'レビュー投稿日',
    selector: row => row.review_date,
    sortable: false,
    width: '160px',
    cell: row => (
      <div>
        <span className="fw-bold text-dark fs-11px mt-1.5 d-block text-truncate" style={{ maxWidth: '130px' }} title={row.nickname}>
          <i className="fa fa-user me-1 text-muted"></i>{row.nickname || '名無しさん'}
        </span>
        <span className="text-secondary d-block">
          {row.review_date ? row.review_date.replace('T', ' ').substring(0, 16) : ''}
        </span>
      </div>
    )
  },
  {
    name: '評価',
    selector: row => row.rating,
    sortable: false,
    width: '140px',
    cell: row => (
      <div>
        <div style={{ color: '#ffc107', fontSize: '16px' }}>
          {'★'.repeat(row.rating)}{'☆'.repeat(5 - row.rating)}
        </div>
        {row.helpful_count > 0 && (
          <div className="mt-1 fs-11px text-muted">
            <i className="fa fa-thumbs-up me-1 text-success"></i>{row.helpful_count}人が参考になった
          </div>
        )}
      </div>
    ),
  },
  {
    name: 'レビュー内容',
    selector: row => row.comment,
    wrap: true,
    cell: row => (
      <div style={{ padding: '12px 0', width: '100%' }}>
        {!row.is_shop_review && row.product_url && (
          <div className="mb-1.5 fs-11px">
            <a href={row.product_url} target="_blank" rel="noreferrer" className="text-primary text-decoration-none text-truncate d-block" style={{ maxWidth: '450px' }}>
              <i className="fa fa-shopping-bag me-1"></i>{row.product_name}
            </a>
          </div>
        )}
        <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px', color: '#1f2d3d' }}>{row.title}</div>
        <div style={{ color: '#495057', fontSize: '13px', lineHeight: '1.5', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>{row.comment}</div>

        <div className="d-flex flex-wrap gap-1 mb-2">
          {row.purchased_color && <span className="badge bg-light text-dark border fs-10px">カラー: {row.purchased_color}</span>}
          {row.purchased_size && <span className="badge bg-light text-dark border fs-10px">サイズ: {row.purchased_size}</span>}
          {row.user_reviewer_info && <span className="badge bg-info text-white fs-10px">{row.user_reviewer_info}</span>}
          {row.review_text_usage && (
            <span className="badge bg-purple text-white fs-10px">
              <i className="fa fa-tags me-1"></i>{row.review_text_usage}
            </span>
          )}
        </div>

        {row.media_urls && row.media_urls.length > 0 && (
          <div className="d-flex flex-wrap gap-1 mt-2">
            {row.media_urls.map((imgUrl, idx) => (
              <a href={imgUrl} target="_blank" rel="noreferrer" key={idx}>
                <img
                  src={imgUrl}
                  alt={`ユーザー投稿-${idx}`}
                  style={{ width: '45px', height: '45px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd', cursor: 'zoom-in' }}
                />
              </a>
            ))}
          </div>
        )}
      </div>
    ),
  },
];

const ReviewList = () => {
  const { mall } = useParams();
  const context = useContext(AppSettings);

  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 各種フィルター＆検索の状態管理
  const [selectedStore, setSelectedStore] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [sortOrder, setSortOrder] = useState('date_desc');
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentShops, setCurrentShops] = useState([]);

  // 🌟 【新規追加】サイズとカラーの絞り込み状態
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');

  // ページネーション用の状態管理
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // チェックボックスで選択された行のデータを保存するステート
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggledClearRows, setToggledClearRows] = useState(false);

  // モール切り替え時の初期化
  useEffect(() => {
    setSelectedStore('all');
    setSelectedRating('all');
    setSortOrder('date_desc');
    setSearchInput('');
    setSearchKeyword('');
    setSelectedSize('');  // 🌟 追加
    setSelectedColor(''); // 🌟 追加
    setCurrentPage(1);
    setToggledClearRows(!toggledClearRows);

    if (context.userInfo && context.userInfo.malls) {
      const activeMall = context.userInfo.malls.find(m => m.path.endsWith(`/${mall}`));
      if (activeMall && activeMall.shops) {
        setCurrentShops(activeMall.shops);
      } else {
        setCurrentShops([]);
      }
    }
  }, [mall, context.userInfo]);

  // データ取得（API通信）
  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      try {
        let url = `/reviews/${mall}?shop_uuid=${selectedStore}&page=${currentPage}&per_page=${perPage}&sort_by=${sortOrder}`;
        if (selectedRating !== 'all') url += `&rating=${selectedRating}`;
        if (searchKeyword) url += `&keyword=${encodeURIComponent(searchKeyword)}`;

        // 🌟 【新規追加】パラメータに size と color を追加
        if (selectedSize) url += `&size=${encodeURIComponent(selectedSize)}`;
        if (selectedColor) url += `&color=${encodeURIComponent(selectedColor)}`;

        const response = await apiFetch(url);

        if (!response.ok) throw new Error('データの取得に失敗しました');

        const data = await response.json();
        setFilteredData(data.items);
        setTotalRows(data.total);
      } catch (error) {
        console.error("API Error:", error);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
    // 🌟 依存配列に selectedSize と selectedColor を追加
  }, [mall, selectedStore, selectedRating, searchKeyword, sortOrder, currentPage, perPage, selectedSize, selectedColor]);

  // 検索ボックスの「即時反映（デバウンス）」ロジック
  useEffect(() => {
    if (searchInput.length >= 2 || searchInput.length === 0) {
      const timerId = setTimeout(() => {
        if (searchKeyword !== searchInput) {
          setSearchKeyword(searchInput);
          setSelectedSize('');  // 🌟 キーワードが変わったらサイズ条件をリセット
          setSelectedColor(''); // 🌟 キーワードが変わったらカラー条件をリセット
          setCurrentPage(1);
          setToggledClearRows(!toggledClearRows);
        }
      }, 500);
      return () => clearTimeout(timerId);
    }
  }, [searchInput]);

  const handlePageChange = page => setCurrentPage(page);
  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // CSV / JSON のダウンロード機能
  const handleDownload = async (format) => {
    try {
      let itemsToDownload = [];

      if (selectedRows.length > 0) {
        itemsToDownload = selectedRows;
      } else {
        // ダウンロード時も、サイズとカラーの絞り込み条件を反映させる
        let url = `/reviews/${mall}?shop_uuid=${selectedStore}&page=1&per_page=10000&sort_by=${sortOrder}`;
        if (selectedRating !== 'all') url += `&rating=${selectedRating}`;
        if (searchKeyword) url += `&keyword=${encodeURIComponent(searchKeyword)}`;
        if (selectedSize) url += `&size=${encodeURIComponent(selectedSize)}`;    // 🌟 追加
        if (selectedColor) url += `&color=${encodeURIComponent(selectedColor)}`;  // 🌟 追加

        const response = await apiFetch(url);
        const data = await response.json();
        itemsToDownload = data.items;
      }

      if (format === 'json') {
        const blob = new Blob([JSON.stringify(itemsToDownload, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `reviews_${mall}_${new Date().getTime()}.json`;
        a.click();

      } else if (format === 'csv') {
        const headers = ["ID", "種別", "商品名", "ニックネーム", "投稿日", "星", "タイトル", "本文", "いいね数", "店舗返信", "サイズ", "カラー"];
        const csvRows = [headers.join(',')];

        itemsToDownload.forEach(row => {
          const escapeCsv = (str) => `"${(str || '').toString().replace(/"/g, '""')}"`;
          const values = [
            row.id,
            row.is_shop_review ? "ショップ" : "商品",
            escapeCsv(row.product_name),
            escapeCsv(row.nickname),
            row.review_date,
            row.rating,
            escapeCsv(row.title),
            escapeCsv(row.comment),
            row.helpful_count || 0,
            escapeCsv(row.shop_reply),
            escapeCsv(row.purchased_size),  // 🌟 CSVにサイズを追加
            escapeCsv(row.purchased_color)  // 🌟 CSVにカラーを追加
          ];
          csvRows.push(values.join(','));
        });

        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csvRows.join('\n')], { type: 'text/csv' });

        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `reviews_${mall}_${new Date().getTime()}.csv`;
        a.click();
      }

      setToggledClearRows(!toggledClearRows);

    } catch (error) {
      alert("ダウンロードに失敗しました。時間をおいて再試行してください。");
      console.error(error);
    }
  };

  const displayMallName = {
    rakuten: '楽天市場',
    amazon: 'Amazon',
    yahoo: 'Yahoo!ショッピング'
  }[mall] || 'すべてのモール';

  const downloadTextPrefix = selectedRows.length > 0 ? `選択した${selectedRows.length}件を` : '条件一致すべてを';

  // 🌟 【新規追加】画面に表示中のデータから存在するサイズ・色を自動抽出
  // ※ null や undefined は filter(Boolean) で除去し、重複を Set で消します
  const availableSizes = [...new Set(filteredData.map(item => item.purchased_size).filter(Boolean))].sort();
  const availableColors = [...new Set(filteredData.map(item => item.purchased_color).filter(Boolean))].sort();

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item active">レビュー管理</li>
      </ol>
      <h1 className="page-header">
        {displayMallName}のレビュー分析 <small>クチコミの抽出とエクスポート</small>
      </h1>

      <div className="panel panel-inverse">
        <div className="panel-heading d-flex flex-column flex-md-row align-items-md-center">
          <h4 className="panel-title flex-grow-1 mb-2 mb-md-0">クチコミ一覧</h4>

          <div className="d-flex flex-column flex-md-row gap-2">
            <div className="d-flex gap-2 ms-md-3 order-md-2">
              <button className="btn btn-sm btn-outline-success bg-white fw-bold" onClick={() => handleDownload('csv')} title="チェックがない場合は現在の検索条件の全件を出力します">
                <i className="fa fa-file-csv me-1 text-success"></i>{downloadTextPrefix}CSV出力
              </button>
              <button className="btn btn-sm btn-outline-warning bg-white fw-bold" onClick={() => handleDownload('json')} title="チェックがない場合は現在の検索条件の全件を出力します">
                <i className="fa fa-code me-1 text-warning"></i>{downloadTextPrefix}JSON出力
              </button>
            </div>

            <div className="d-flex gap-2 flex-wrap order-md-1">
              <select className="form-select form-select-sm" style={{ width: '160px' }} value={selectedStore}
                onChange={(e) => {
                  setSelectedStore(e.target.value);
                  setSelectedSize(''); // リセット
                  setSelectedColor(''); // リセット
                  setCurrentPage(1);
                  setToggledClearRows(!toggledClearRows);
                }}>
                <option value="all">全店舗</option>
                {currentShops.map((shop, index) => (
                  <option key={shop.uuid || shop.id || index} value={shop.uuid || shop.id}>{shop.name}</option>
                ))}
              </select>

              <select className="form-select form-select-sm" style={{ width: '120px' }} value={selectedRating}
                onChange={(e) => { setSelectedRating(e.target.value); setCurrentPage(1); setToggledClearRows(!toggledClearRows); }}>
                <option value="all">全評価</option>
                <option value="5">★★★★★ (5)</option>
                <option value="4">★★★★☆ (4)</option>
                <option value="3">★★★☆☆ (3)</option>
                <option value="2">★★☆☆☆ (2)</option>
                <option value="1">★☆☆☆☆ (1)</option>
              </select>

              <select className="form-select form-select-sm border-primary" style={{ width: '150px' }} value={sortOrder}
                onChange={(e) => { setSortOrder(e.target.value); setCurrentPage(1); setToggledClearRows(!toggledClearRows); }}>
                <option value="date_desc">🕒 新着順</option>
                <option value="helpful_desc">👍 いいねが多い順</option>
                <option value="rating_desc">⭐ 評価が高い順</option>
                <option value="rating_asc">⚠️ 評価が低い順</option>
                <option value="product_asc">📦 商品ごとにまとめる</option>
              </select>

              <div className="input-group input-group-sm" style={{ width: '220px' }}>
                <input type="text" className="form-control" placeholder="商品名・品番・レビューを検索..."
                  value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearchKeyword(searchInput);
                      setSelectedSize(''); // リセット
                      setSelectedColor(''); // リセット
                      setCurrentPage(1);
                      setToggledClearRows(!toggledClearRows);
                    }
                  }} />
                <button className="btn btn-dark" type="button" onClick={() => {
                  setSearchKeyword(searchInput);
                  setSelectedSize(''); // リセット
                  setSelectedColor(''); // リセット
                  setCurrentPage(1);
                  setToggledClearRows(!toggledClearRows);
                }}>
                  <i className="fa fa-search"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="panel-body">
          {/* 🌟 【新規追加】サイズ・カラーのピルボタン表示エリア */}
          {/* 検索時、またはサイズ・色がすでに選択されている時、かつ抽出された選択肢がある時に表示 */}
          {(searchKeyword || selectedSize || selectedColor) && (availableSizes.length > 0 || availableColors.length > 0) && (
            <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#f4f6f9', border: '1px solid #e3e6ec' }}>

              {/* サイズ絞り込み */}
              {(availableSizes.length > 0 || selectedSize) && (
                <div className="d-flex align-items-center flex-wrap gap-2 fs-12px">
                  <span className="fw-bold text-dark" style={{ minWidth: '90px' }}><i className="fa fa-filter me-1 text-muted"></i>サイズ:</span>
                  <button
                    className={`btn btn-xs rounded-pill px-3 ${selectedSize === '' ? 'btn-secondary' : 'btn-outline-secondary bg-white'}`}
                    onClick={() => { setSelectedSize(''); setCurrentPage(1); }}
                  >
                    すべて
                  </button>
                  {/* もし選択中のサイズが現在のページに見当たらなくてもボタンを残す */}
                  {selectedSize && !availableSizes.includes(selectedSize) && (
                    <button
                      className="btn btn-xs rounded-pill px-3 btn-primary"
                      onClick={() => { setSelectedSize(selectedSize); setCurrentPage(1); }}
                    >
                      {selectedSize}
                    </button>
                  )}
                  {availableSizes.map(size => (
                    <button
                      key={size}
                      className={`btn btn-xs rounded-pill px-3 ${selectedSize === size ? 'btn-primary' : 'btn-outline-primary bg-white text-dark'}`}
                      onClick={() => { setSelectedSize(size); setCurrentPage(1); }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}

              {/* カラー絞り込み */}
              {(availableColors.length > 0 || selectedColor) && (
                <div className={`d-flex align-items-center flex-wrap gap-2 fs-12px ${(availableSizes.length > 0 || selectedSize) ? 'mt-2 pt-2 border-top' : ''}`}>
                  <span className="fw-bold text-dark" style={{ minWidth: '90px' }}><i className="fa fa-palette me-1 text-muted"></i>カラー:</span>
                  <button
                    className={`btn btn-xs rounded-pill px-3 ${selectedColor === '' ? 'btn-secondary' : 'btn-outline-secondary bg-white'}`}
                    onClick={() => { setSelectedColor(''); setCurrentPage(1); }}
                  >
                    すべて
                  </button>
                  {/* もし選択中のカラーが現在のページに見当たらなくてもボタンを残す */}
                  {selectedColor && !availableColors.includes(selectedColor) && (
                    <button
                      className="btn btn-xs rounded-pill px-3 btn-info text-white"
                      onClick={() => { setSelectedColor(selectedColor); setCurrentPage(1); }}
                    >
                      {selectedColor}
                    </button>
                  )}
                  {availableColors.map(color => (
                    <button
                      key={color}
                      className={`btn btn-xs rounded-pill px-3 ${selectedColor === color ? 'btn-info text-white' : 'btn-outline-info bg-white text-dark'}`}
                      onClick={() => { setSelectedColor(color); setCurrentPage(1); }}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            progressComponent={
              <div className="text-center p-5">
                <div className="spinner-border text-primary mb-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="text-muted fs-12px">データを読み込み中...</div>
              </div>
            }
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[5, 10, 20, 50, 100]}
            paginationComponentOptions={{ rowsPerPageText: '1ページあたりの件数:', rangeSeparatorText: 'の', }}
            highlightOnHover
            striped
            responsive
            customStyles={customDataTableStyles}
            selectableRows
            onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
            clearSelectedRows={toggledClearRows}
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            expandableRowDisabled={row => !row.shop_reply}
            expandableIcon={{
              collapsed: (<div className="d-flex align-items-center justify-content-center h-100"><i className="fa fa-comment-dots text-gray-500 fs-26px" title="店舗返信あり（クリックで展開）"></i></div>),
              expanded: (<div className="d-flex align-items-center justify-content-center h-100"><i className="fa fa-comments text-primary fs-26px" title="クリックで閉じる"></i></div>)
            }}
            noDataComponent={<div className="text-center p-5 text-muted"><i className="fa fa-folder-open fa-2x mb-2 d-block"></i>表示するレコードがありません</div>}
          />
        </div>
      </div>
    </div>
  );
};

export default ReviewList;